import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/base/Navbar';
import Footer from '@/components/base/Footer';
import SEO from '@/components/base/SEO';
import Breadcrumb from '@/components/base/Breadcrumb';
import MovieCard from '@/components/feature/MovieCard';
import { movies, articles } from '@/mocks/movies';
import { getWebPageSchema, getBreadcrumbSchema, getArticleSchema } from '@/utils/schema';

export default function Article() {
  const { id } = useParams<{ id: string }>();

  const movie = movies.find((m) => m.id === id);
  const article = articles.find((a) => a.id === id);

  const content = movie || article;
  const isMovie = !!movie;

  if (!content) {
    return (
      <>
        <Navbar />
        <main className="max-w-7xl mx-auto px-4 md:px-6 py-20 text-center">
          <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4">
            <i className="ri-error-warning-line text-4xl text-foreground-600"></i>
          </div>
          <h1 className="text-xl font-heading text-foreground-200 mb-2">内容未找到</h1>
          <p className="text-sm text-foreground-500 mb-6">您访问的页面不存在或已被移除。</p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer"
          >
            返回首页
          </Link>
        </main>
        <Footer />
      </>
    );
  }

  const categoryName = isMovie
    ? { movie: '电影', tv: '电视剧', variety: '综艺', anime: '动漫', documentary: '纪录片' }[movie.category] || '影视'
    : { movie: '电影', tv: '电视剧', variety: '综艺', anime: '动漫' }[article.category] || '影视';

  const pageTitle = isMovie ? movie.title : article.title;
  const pageDesc = isMovie ? movie.description : article.summary;
  const pageImage = isMovie ? movie.image : article.image;
  const pageDate = isMovie ? movie.updateDate : article.date;
  const pageAuthor = isMovie ? '春色影视' : article.author;

  const breadcrumbItems = [
    { label: '首页', to: '/' },
    { label: categoryName, to: `/category/${isMovie ? movie.category : article.category}` },
    { label: pageTitle },
  ];

  const relatedMovies = isMovie
    ? movies.filter((m) => m.category === movie.category && m.id !== movie.id).slice(0, 6)
    : movies.filter((m) => m.category === article.category).slice(0, 6);

  const webPageSchema = getWebPageSchema(
    pageTitle,
    pageDesc,
    `https://chunse.yingshi.com/article/${id}`,
    getBreadcrumbSchema([
      { name: '首页', url: 'https://chunse.yingshi.com' },
      { name: categoryName, url: `https://chunse.yingshi.com/category/${isMovie ? movie.category : article.category}` },
      { name: pageTitle, url: `https://chunse.yingshi.com/article/${id}` },
    ]).itemListElement
  );

  const articleSchema = getArticleSchema(
    pageTitle,
    pageDesc,
    `https://chunse.yingshi.com/article/${id}`,
    pageImage,
    pageDate,
    pageAuthor,
  );

  return (
    <>
      <SEO
        title={pageTitle}
        description={pageDesc}
        keywords={`${pageTitle},春色影视,在线观看,高清${categoryName},免费${categoryName}`}
        canonicalUrl={`https://chunse.yingshi.com/article/${id}`}
        ogType="article"
        ogImage={pageImage}
        schema={{ ...webPageSchema, ...articleSchema }}
      />

      <Navbar />

      <main className="max-w-7xl mx-auto px-4 md:px-6 py-6">
        <Breadcrumb items={breadcrumbItems} />

        <article className="grid grid-cols-1 lg:grid-cols-3 gap-8 mt-4">
          {/* Main Content */}
          <div className="lg:col-span-2">
            {isMovie ? (
              <>
                <div className="flex flex-col sm:flex-row gap-6 mb-8">
                  <div className="w-40 h-56 shrink-0 rounded-lg overflow-hidden mx-auto sm:mx-0">
                    <img
                      src={movie.image}
                      alt={movie.title}
                      title={`${movie.title} - 春色影视高清在线观看`}
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                  <div className="flex flex-col justify-between min-w-0">
                    <div>
                      <h1 className="text-2xl md:text-3xl font-heading text-foreground-100 mb-3">
                        {movie.title}
                      </h1>
                      <div className="flex flex-wrap items-center gap-2 mb-3">
                        <span className="flex items-center gap-1 px-2.5 py-1 rounded-full bg-accent-500/20 text-accent-500 text-xs font-medium whitespace-nowrap">
                          <span className="w-3 h-3 flex items-center justify-center">
                            <i className="ri-star-fill text-[10px]"></i>
                          </span>
                          {movie.rating} 分
                        </span>
                        <span className="px-2.5 py-1 rounded-full bg-background-100 text-foreground-400 text-xs font-medium border border-background-200/50 whitespace-nowrap">
                          {movie.year}
                        </span>
                        <span className="px-2.5 py-1 rounded-full bg-background-100 text-foreground-400 text-xs font-medium border border-background-200/50 whitespace-nowrap">
                          {movie.duration}
                        </span>
                        <span className="px-2.5 py-1 rounded-full bg-background-100 text-foreground-400 text-xs font-medium border border-background-200/50 whitespace-nowrap">
                          {movie.region}
                        </span>
                      </div>
                    </div>

                    <div className="space-y-2 text-sm">
                      <div className="flex gap-2">
                        <span className="text-foreground-600 shrink-0 whitespace-nowrap">导演：</span>
                        <span className="text-foreground-300">{movie.director}</span>
                      </div>
                      <div className="flex gap-2">
                        <span className="text-foreground-600 shrink-0 whitespace-nowrap">主演：</span>
                        <span className="text-foreground-300">{movie.actors.join(' / ')}</span>
                      </div>
                      <div className="flex gap-2">
                        <span className="text-foreground-600 shrink-0 whitespace-nowrap">类型：</span>
                        <span className="text-foreground-300">{movie.type}</span>
                      </div>
                      <div className="flex gap-2">
                        <span className="text-foreground-600 shrink-0 whitespace-nowrap">更新：</span>
                        <span className="text-foreground-300">{movie.updateDate}</span>
                      </div>
                    </div>

                    <div className="flex items-center gap-3 mt-4">
                      <button className="flex items-center gap-2 px-6 py-2.5 rounded-lg bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-semibold transition-all duration-200 whitespace-nowrap cursor-pointer">
                        <span className="w-4 h-4 flex items-center justify-center">
                          <i className="ri-play-circle-fill"></i>
                        </span>
                        立即播放
                      </button>
                      <button className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-background-100 hover:bg-background-200/70 text-foreground-300 text-sm font-medium border border-background-200/50 transition-all duration-200 whitespace-nowrap cursor-pointer">
                        <span className="w-4 h-4 flex items-center justify-center">
                          <i className="ri-heart-line"></i>
                        </span>
                        收藏
                      </button>
                    </div>
                  </div>
                </div>

                <div className="mb-6">
                  <h2 className="text-lg font-heading text-foreground-200 mb-3">
                    <a href="#synopsis">剧情简介</a>
                  </h2>
                  <p className="text-sm text-foreground-400 leading-relaxed">{movie.description}</p>
                </div>

                <div className="flex flex-wrap gap-2 mb-8">
                  {movie.tags.map((tag) => (
                    <span
                      key={tag}
                      className="px-3 py-1 rounded-full bg-secondary-500/20 text-secondary-500 text-xs font-medium border border-secondary-500/30 whitespace-nowrap"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </>
            ) : (
              <>
                <img
                  src={article.image}
                  alt={article.title}
                  title={article.title}
                  className="w-full rounded-lg mb-6 object-cover"
                />
                <h1 className="text-2xl md:text-3xl font-heading text-foreground-100 mb-3">
                  {article.title}
                </h1>
                <div className="flex items-center gap-4 text-xs text-foreground-500 mb-6">
                  <span>{article.date}</span>
                  <span>{article.author}</span>
                </div>
                <div
                  className="text-sm text-foreground-300 leading-relaxed space-y-4"
                  dangerouslySetInnerHTML={{ __html: article.content }}
                />
                <div className="flex flex-wrap gap-2 mt-6 mb-8">
                  {article.tags.map((tag) => (
                    <span
                      key={tag}
                      className="px-3 py-1 rounded-full bg-secondary-500/20 text-secondary-500 text-xs font-medium border border-secondary-500/30 whitespace-nowrap"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </>
            )}

            {/* Related Content */}
            {relatedMovies.length > 0 && (
              <section className="border-t border-background-200/50 pt-8">
                <h3 className="text-lg font-heading text-foreground-200 mb-4">
                  <a href="#related">相关推荐</a>
                </h3>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-3 gap-4">
                  {relatedMovies.map((m) => (
                    <MovieCard key={m.id} movie={m} />
                  ))}
                </div>
              </section>
            )}
          </div>

          {/* Sidebar */}
          <aside className="lg:col-span-1">
            <div className="sticky top-20 space-y-6">
              <div className="rounded-lg bg-background-100 border border-background-200/50 p-5">
                <h3 className="text-sm font-semibold text-foreground-200 mb-4">
                  <a href="#hot-sidebar">热门推荐</a>
                </h3>
                <div className="space-y-3">
                  {movies.filter((m) => m.isHot).slice(0, 5).map((m) => (
                    <Link
                      key={m.id}
                      to={`/article/${m.id}`}
                      className="flex gap-3 group cursor-pointer"
                    >
                      <div className="w-16 h-20 shrink-0 rounded-md overflow-hidden">
                        <img
                          src={m.image}
                          alt={m.title}
                          className="w-full h-full object-cover object-top"
                        />
                      </div>
                      <div className="flex flex-col justify-center min-w-0">
                        <p className="text-sm text-foreground-300 group-hover:text-primary-500 transition-colors duration-200 line-clamp-2">
                          {m.title}
                        </p>
                        <span className="flex items-center gap-1 text-xs text-accent-500 mt-1">
                          <span className="w-3 h-3 flex items-center justify-center">
                            <i className="ri-star-fill text-[10px]"></i>
                          </span>
                          {m.rating}
                        </span>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>

              <div className="rounded-lg bg-background-100 border border-background-200/50 p-5">
                <h3 className="text-sm font-semibold text-foreground-200 mb-4">
                  <a href="#tags-sidebar">热门标签</a>
                </h3>
                <div className="flex flex-wrap gap-2">
                  {['科幻大片', '国产巨制', '口碑佳作', '热播剧', '高人气', '高分', '动作', '悬疑', '喜剧', '励志'].map((tag) => (
                    <span
                      key={tag}
                      className="px-3 py-1 rounded-full bg-secondary-500/20 text-secondary-500 text-xs font-medium border border-secondary-500/30 cursor-pointer hover:bg-secondary-500/30 transition-colors duration-200 whitespace-nowrap"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </aside>
        </article>
      </main>

      <Footer />
    </>
  );
}